/**
 * Recipe FORWARD sizing resource — typed wrapper over
 * `GET /v1/twin/recipes/{recipe_id}/sizing?qty=N` plus its React Query hook
 * (Phase-2 Wave-0.5 item 4; backend `internal/twin/forward_sizing.go`).
 *
 * The forward chain answers "how much metal do I charge to get N castings?":
 *   designed × cavities + rigging → poured/mould → required_poured →
 *   required_tapped (pour loss) → required_melt → required_charge (melt loss).
 * `required_charge_kg` is the headline answer.
 *
 * HONESTY (mirrors the Go contract exactly — do not soften it in the UI):
 *   - Every mass term is a `SizingTerm { value, tier, basis }`. `value === null`
 *     means WITHHELD (the contagious-unknown propagated to that term) and MUST
 *     render "—" — never 0, never a default, never a fabricated number.
 *   - The forward OUTPUTS wear `tier: "PLANNED"` — a plan, never a measurement.
 *     Inputs (designed / rigging / pour-loss / melt-loss) wear "ESTIMATED", and
 *     a withheld term wears "UNKNOWN".
 *   - `withheld: true` means the ANSWER (required_charge) itself was withheld.
 *   - `basis` discloses where the number (or the withholding) came from — the
 *     UI surfaces it as the value's tooltip.
 *
 * Shape/auth follow the sibling twin-* modules (snake_case wire shape, the
 * shared `api` client, the same live/auth/org gate).
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { qs } from "./qs";

/**
 * Provenance tier a sizing term carries, as the backend spells it. Known
 * values: `PLANNED` (a forward planning output — a target, NOT a measurement),
 * `ESTIMATED` (a derived/template input), `UNKNOWN` / `WITHHELD` (no value).
 * `METERED` / `CALCULATED` never appear on THIS endpoint but are part of the
 * same vocabulary. Kept as a widened `string` on purpose: an unrecognised tier
 * from a newer backend must still render (as the honest "unknown" chip) rather
 * than break the type.
 */
export type SizingTier = string;

/**
 * One term of the forward chain. `value: null` ⇒ WITHHELD (render "—").
 * `basis` is the disclosure string for the value OR for the withholding.
 */
export interface SizingTerm {
  value: number | null;
  tier: SizingTier;
  basis: string;
}

/** The full PLANNED sizing for a recipe at a given quantity. */
export interface ForwardSizing {
  recipe_id: string;
  grade: string;
  metal: string;
  metal_route: string;
  /** The qty actually sized for (the `?qty=` param or the recipe's planned_qty). */
  planned_qty: number;
  /** "query_param" when `?qty=` drove it, else "recipe_planned_qty". */
  qty_source: string;

  /** Castings per mould. */
  cavities: number;
  cavities_basis: string;
  /** ceil(planned_qty / cavities). */
  moulds: number;
  /** moulds × cavities — the PRODUCIBLE count (≥ planned_qty). */
  castings: number;

  designed_per_casting_kg: SizingTerm;
  rigging_per_mould_kg: SizingTerm;
  poured_per_mould_kg: SizingTerm;
  required_poured_kg: SizingTerm;
  required_tapped_kg: SizingTerm;
  required_melt_kg: SizingTerm;
  /** THE ANSWER — melt/charge this much for this planned quantity. */
  required_charge_kg: SizingTerm;

  /** Percent (not a fraction) — template default 3% or a rated override. */
  pour_loss_pct: SizingTerm;
  /** Percent (not a fraction) — per-role (P4) / per-metal / rated. */
  melt_loss_pct: SizingTerm;

  /** "PLANNED" — the whole model is a plan. */
  tier: SizingTier;
  /** true iff required_charge (the answer) was withheld. */
  withheld: boolean;
  note: string;
}

export const twinSizing = {
  /**
   * Forward sizing for one recipe. `qty` overrides the recipe's planned_qty;
   * omit it to size for the recipe's own planned quantity.
   */
  get: (recipeId: string, qty?: number) =>
    api.get<ForwardSizing>(
      `/v1/twin/recipes/${encodeURIComponent(recipeId)}/sizing${qs({ qty })}`,
    ),
};

/** Build-time flag: only when explicitly "true" do live fetches fire. */
const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live fetches require the build flag AND an authenticated, org-resolved session. */
function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** Query key for one recipe's sizing at a quantity. */
export const twinSizingKey = (recipeId: string, qty?: number) =>
  ["twin-recipe-sizing", recipeId, qty ?? null] as const;

/**
 * The forward sizing for `recipeId` at `qty`. Disabled until a recipe is
 * picked; a non-positive qty is not sent (the backend then sizes for the
 * recipe's planned_qty, and 400s when it has none).
 */
export function useRecipeSizingQuery(recipeId: string | null, qty?: number) {
  const live = useLiveEnabled();
  const effectiveQty = qty !== undefined && qty > 0 ? qty : undefined;
  return useQuery({
    queryKey: twinSizingKey(recipeId ?? "", effectiveQty),
    queryFn: () => twinSizing.get(recipeId as string, effectiveQty),
    enabled: live && !!recipeId,
    staleTime: 60_000,
    retry: false,
  });
}
